import { reverseGeocode } from "@/lib/googleApi.ts";
import {
  getCurrentLocation,
  getLocationPermissionState,
  type LocationSuggestion,
  searchPlaces,
} from "@/lib/places.ts";
import { cn, debounce } from "@/lib/utils.ts";
import * as Sentry from "@sentry/react";
import { ArrowUp, Check, MapPin, Navigation, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useState } from "react";

export interface LocationSearchProps {
  value: string;
  onChange: (value: string) => void;
  onLocationSelect: (location: LocationSuggestion) => void;
  placeholder?: string;
  disabled?: boolean;
  showCurrentLocation?: boolean;
  showDropdown?: boolean;
  className?: string;
  icon?: React.ReactNode;
  showIcon?: boolean;
  /** Small stacked label rendered inside the field, above the input line. */
  label?: string;
  // Optional save/cancel functionality
  showSaveCancel?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  originalValue?: string; // Used to reset on cancel
  isSaving?: boolean;
  // Optional delete functionality
  onDelete?: () => void;
  // Optional submit functionality (for onboarding-style inputs)
  showSubmit?: boolean;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  showSubmitButton?: boolean;
  ref?: React.RefObject<HTMLInputElement | null>;
}

export const LocationSearch = ({
  value,
  onChange,
  onLocationSelect,
  placeholder = "Search for a location",
  disabled = false,
  showCurrentLocation = true,
  showDropdown = true,
  className = "",
  icon,
  showIcon = true,
  label,
  showSaveCancel = false,
  onSave,
  onCancel,
  originalValue,
  isSaving = false,
  onDelete,
  showSubmit = false,
  onSubmit,
  isSubmitting = false,
  showSubmitButton = false,
  ref,
}: LocationSearchProps) => {
  const [suggestions, setSuggestionsState] = useState<LocationSuggestion[]>([]);
  // Keyboard highlight over the dropdown rows: index 0 is the current-location
  // row when it renders, then the suggestions in order. -1 means nothing
  // highlighted, which keeps Enter free for the field's own submit behavior.
  const [activeIndex, setActiveIndex] = useState(-1);
  // Every change to the option list goes through here: a kept highlight would
  // point at a different row once an async Places response lands, so Enter
  // could pick an address the user never highlighted.
  const setSuggestions = useCallback((next: LocationSuggestion[]) => {
    setSuggestionsState(next);
    setActiveIndex(-1);
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [permissionState, setPermissionState] = useState<PermissionState>("prompt");
  const [showPermissionHelp, setShowPermissionHelp] = useState(false);

  // Check permission state on mount
  useEffect(() => {
    getLocationPermissionState().then(setPermissionState);
  }, []);

  // Debounced Google Maps Places API search
  const performPlacesSearch = useCallback(
    async (query: string) => {
      if (query.length < 3) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);

      try {
        const results = await searchPlaces(query, 5);
        setSuggestions(results);
      } catch (error) {
        console.error("Error searching places:", error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [setSuggestions],
  );

  const debouncedPlacesSearch = useMemo(
    () => debounce(performPlacesSearch, 300),
    [performPlacesSearch],
  );
  // A search still waiting when the field goes would set state on nothing.
  useEffect(() => () => debouncedPlacesSearch.cancel(), [debouncedPlacesSearch]);

  const handleLocationSearch = useCallback(
    (query: string) => {
      onChange(query);
      setLocationError(null);
      debouncedPlacesSearch(query);
    },
    [onChange, debouncedPlacesSearch],
  );

  const handleLocationSelect = useCallback(
    (location: LocationSuggestion) => {
      onChange(location.address);
      setSuggestions([]);
      setLocationError(null);
      setIsInputFocused(false);
      onLocationSelect(location);
    },
    [onChange, onLocationSelect, setSuggestions],
  );

  const handleCurrentLocationSelect = useCallback(async () => {
    setIsGettingLocation(true);
    setLocationError(null);
    setShowPermissionHelp(false);
    setSuggestions([]); // Hide suggestions while processing

    try {
      const currentPermissionState = await getLocationPermissionState();
      setPermissionState(currentPermissionState);

      // Get the user's current coordinates
      const currentLocation = await getCurrentLocation();

      // If we have coordinates, reverse geocode to get a human-readable address
      if (currentLocation.location) {
        const { latitude, longitude } = currentLocation.location;
        const address = await reverseGeocode(latitude, longitude);

        // Populate the input field with the resolved address instead of "Current location"
        onChange(address);
        onLocationSelect({
          id: "current-location",
          name: address,
          address,
          location: currentLocation.location,
          placeId: "current-location",
        });
      } else {
        // Fallback if no coordinates available
        onChange(currentLocation.name);
        onLocationSelect({
          id: "current-location",
          name: currentLocation.name,
          address: currentLocation.name,
          location: currentLocation.location,
          placeId: "current-location",
        });
      }

      setIsInputFocused(false); // Close the dropdown
      setPermissionState("granted");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to get current location";
      setLocationError(errorMessage);

      const updatedPermissionState = await getLocationPermissionState();
      setPermissionState(updatedPermissionState);

      if (updatedPermissionState === "denied") {
        setShowPermissionHelp(true);
      }

      Sentry.captureException(error, {
        tags: {
          component: "LocationSearch",
          function: "handleCurrentLocationSelect",
        },
        extra: {
          permissionState: updatedPermissionState,
          userAgent: navigator.userAgent,
        },
      });
    } finally {
      setIsGettingLocation(false);
    }
  }, [onChange, onLocationSelect, setSuggestions]);

  const handleRetryLocation = useCallback(async () => {
    setLocationError(null);
    setShowPermissionHelp(false);
    await handleCurrentLocationSelect();
  }, [handleCurrentLocationSelect]);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave();
    }
  }, [onSave]);

  const handleCancel = useCallback(() => {
    if (originalValue !== undefined) {
      onChange(originalValue);
    }
    if (onCancel) {
      onCancel();
    }
  }, [onCancel, originalValue, onChange]);

  const handleSubmit = useCallback(() => {
    if (onSubmit) {
      onSubmit();
    }
  }, [onSubmit]);

  const getPermissionHelpText = () => {
    switch (permissionState) {
      case "denied":
        return "To use your current location, please enable location permissions in your browser settings. Look for the location icon in your address bar and click 'Allow'.";
      case "granted":
        return null;
      default:
        return null;
    }
  };

  const shouldShowDropdown =
    showDropdown && (suggestions.length > 0 || locationError !== null || isInputFocused);

  const currentLocationShown = shouldShowDropdown && showCurrentLocation;
  const optionCount =
    (currentLocationShown ? 1 : 0) + (shouldShowDropdown ? suggestions.length : 0);
  const listboxId = useId();

  const currentLocationDisabled = isGettingLocation || permissionState === "denied";
  const activateOption = (index: number) => {
    if (currentLocationShown && index === 0) {
      // The rendered row is disabled in these states; Enter must not reach the
      // geolocation path the disabled button blocks for the mouse.
      if (currentLocationDisabled) return;
      void handleCurrentLocationSelect();
    } else {
      const suggestion = suggestions[index - (currentLocationShown ? 1 : 0)];
      if (suggestion) handleLocationSelect(suggestion);
    }
    setActiveIndex(-1);
  };

  // Determine if save button should be shown
  const hasValidInput = value.trim().length > 0;
  const hasChanged = originalValue !== undefined && value !== originalValue;
  const shouldShowSave = showSaveCancel && hasValidInput && hasChanged;

  return (
    <div className="flex flex-row gap-2 items-center">
      <div className="relative flex-1 min-w-0">
        <div
          className={cn(
            "bg-white rounded-md p-2.5 flex flex-row gap-2.5 items-center",
            label ? "min-h-[56px]" : "h-[50px]",
            // `cn` and not a template literal: a caller grouping these fields
            // into one table has to be able to turn the field's own background
            // and rounding off, and string concatenation leaves which one wins
            // to Tailwind's emission order rather than to the caller.
            className,
          )}
        >
          {showIcon && (
            <div className="h-8 flex items-center justify-center">
              {icon || <MapPin className="w-4 h-4 text-zinc-900" />}
            </div>
          )}
          {/* With a label, the field is a stacked pair — small label over the
              input line — inside the same bordered box, so the label travels
              with the field instead of floating above it. */}
          <div className="flex flex-1 min-w-0 flex-col">
            {label && <div className="text-xs text-muted-foreground">{label}</div>}
            <input
              type="text"
              value={value}
              onChange={(e) => handleLocationSearch(e.target.value)}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => {
                // Delay hiding to allow for button clicks
                setTimeout(() => setIsInputFocused(false), 150);
                setActiveIndex(-1);
              }}
              onKeyDown={(e) => {
                if (shouldShowDropdown && optionCount > 0) {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setActiveIndex((i) => (i + 1) % optionCount);
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setActiveIndex((i) => (i <= 0 ? optionCount - 1 : i - 1));
                    return;
                  }
                  if (e.key === "Escape") {
                    setIsInputFocused(false);
                    setActiveIndex(-1);
                    return;
                  }
                  if (e.key === "Enter" && activeIndex >= 0) {
                    // preventDefault marks the Enter as claimed, so wrappers
                    // listening for Enter-to-save don't also commit the text.
                    e.preventDefault();
                    activateOption(activeIndex);
                    return;
                  }
                }
                if (e.key === "Enter" && showSubmit && value.trim().length > 0) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder={placeholder}
              disabled={disabled}
              ref={ref}
              role="combobox"
              aria-expanded={shouldShowDropdown}
              aria-controls={listboxId}
              aria-activedescendant={activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined}
              className="w-full min-w-0 text-sm text-zinc-900 font-normal leading-5 bg-transparent border-none outline-none placeholder-zinc-500/55 disabled:opacity-50"
            />
          </div>
          {isLoading && (
            <div className="flex items-center">
              <div className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
            </div>
          )}
          {showSubmit && value.trim().length > 0 && (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`flex items-center p-2 bg-zinc-100 rounded-full cursor-pointer transition-all duration-500 ${
                showSubmitButton ? "opacity-100 scale-100" : "opacity-0 scale-75"
              }`}
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
              ) : (
                <ArrowUp className="w-4 h-4 text-zinc-400" />
              )}
            </button>
          )}
          {showSaveCancel && shouldShowSave && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              aria-label="Save location"
              className="h-8 flex items-center justify-center p-2 rounded-[7px] hover:bg-green-50 transition-colors disabled:opacity-50"
            >
              <Check className="w-4 h-4 text-green-600" />
            </button>
          )}
          {/* Not gated on showSaveCancel: a field with a remove handler shows
              its remove control. h-8 in the centered row keeps it level with
              the field's leading icon whatever the field's height. */}
          {onDelete && value.length > 0 && (
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setSuggestions([]);
                onDelete();
              }}
              disabled={isSaving}
              aria-label="Clear location"
              className="h-8 flex items-center justify-center p-2 rounded-[7px] hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5 text-destructive/60 hover:text-destructive" />
            </button>
          )}
        </div>

        {/* Location suggestions dropdown */}
        {shouldShowDropdown && (
          <div
            id={listboxId}
            role="listbox"
            className="absolute left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-2xl shadow-lg z-10 overflow-hidden"
          >
            {/* Current location option */}
            {showCurrentLocation && (
              <button
                id={`${listboxId}-0`}
                role="option"
                aria-selected={activeIndex === 0}
                onClick={handleCurrentLocationSelect}
                onMouseEnter={() => setActiveIndex(0)}
                disabled={currentLocationDisabled}
                className={`w-full px-4 py-3 text-left transition-colors disabled:opacity-50 ${
                  activeIndex === 0 ? "bg-gray-50" : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <Navigation
                    className={`w-4 h-4 flex-shrink-0 ${
                      permissionState === "denied"
                        ? "text-red-400"
                        : permissionState === "granted"
                          ? "text-green-500"
                          : "text-zinc-500"
                    }`}
                  />
                  <div>
                    <div className="text-sm font-medium text-zinc-900">
                      {isGettingLocation
                        ? "Getting location..."
                        : permissionState === "denied"
                          ? "Location access denied"
                          : "Current location"}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {isGettingLocation
                        ? "Please wait..."
                        : permissionState === "denied"
                          ? "Enable permissions to use this feature"
                          : "Get your address from your device's location services"}
                    </div>
                  </div>
                </div>
              </button>
            )}

            {/* Permission help */}
            {showPermissionHelp && getPermissionHelpText() && (
              <div className="px-4 py-3 text-sm text-amber-700 bg-amber-50 border-t border-amber-200">
                <div className="mb-2">{getPermissionHelpText()}</div>
                <button
                  onClick={handleRetryLocation}
                  className="text-xs text-blue-600 hover:text-blue-700 underline cursor-pointer"
                >
                  Try again after enabling permissions
                </button>
              </div>
            )}

            {/* Google Places suggestions */}
            {suggestions.map((suggestion, i) => {
              const optionIndex = i + (currentLocationShown ? 1 : 0);
              return (
                <button
                  key={suggestion.id}
                  id={`${listboxId}-${optionIndex}`}
                  role="option"
                  aria-selected={activeIndex === optionIndex}
                  onClick={() => handleLocationSelect(suggestion)}
                  onMouseEnter={() => setActiveIndex(optionIndex)}
                  className={`w-full px-4 py-3 text-left transition-colors ${
                    activeIndex === optionIndex ? "bg-gray-50" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-zinc-900">{suggestion.name}</div>
                      <div className="text-xs text-zinc-500">{suggestion.address}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {showSaveCancel && (
        <button
          onClick={handleCancel}
          disabled={isSaving}
          aria-label="Cancel"
          className="h-10 w-10 shrink-0 flex items-center justify-center rounded-md hover:bg-muted transition-colors disabled:opacity-50"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      )}
    </div>
  );
};
